import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTenantCompliance } from "../../hooks/useTenantCompliance";
import { ComplianceTypeEntity } from "../../../../compliance/domain/entities/ComplianceTypeEntity";
import { Table } from "../../../../../ui/components/Table";
import { Button } from "../../../../../ui/components/Button";
import { Badge } from "../../../../../ui/components/Badge";
import { FeatherArrowLeft, FeatherCheck, FeatherX } from "@subframe/core";
import { useToast } from "../../../../../shared/presentation/components";
import { DefaultPageLayout } from "../../../../../ui/layouts/DefaultPageLayout";

export const ComplianceTypesPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const {
    masterTypes,
    loadingMasterTypes,
    errorMasterTypes,
    fetchMasterTypes,
    enableComplianceType,
    disableComplianceType,
  } = useTenantCompliance();

  // Track loading state per type (for individual button loaders)
  const [enablingTypes, setEnablingTypes] = useState<Set<number>>(new Set());
  const [disablingTypes, setDisablingTypes] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchMasterTypes(); // Single call - includes enabled status
  }, [fetchMasterTypes]);

  const handleEnable = async (type: ComplianceTypeEntity) => {
    // Add to loading set
    setEnablingTypes((prev) => new Set(prev).add(type.id));

    try {
      // Send full type object to backend (already fetched from master)
      // Hook will update local state optimistically - no refresh needed
      await enableComplianceType(type);

      showSuccess(`${type.compliance_name} enabled successfully`);
    } catch (err: any) {
      showError(err.message || "Failed to enable compliance type");
    } finally {
      // Remove from loading set
      setEnablingTypes((prev) => {
        const next = new Set(prev);
        next.delete(type.id);
        return next;
      });
    }
  };

  const handleDisable = async (type: ComplianceTypeEntity) => {
    // Use enabled_id from the type object (if available)
    const enabledId = type.enabled_id;
    if (!enabledId) return;

    if (
      !window.confirm(
        `Are you sure you want to disable ${type.compliance_name}?`
      )
    ) {
      return;
    }

    // Add to loading set
    setDisablingTypes((prev) => new Set(prev).add(type.id));

    try {
      // Hook will update local state optimistically - no refresh needed
      await disableComplianceType(enabledId);
      showSuccess(`${type.compliance_name} disabled successfully`);
    } catch (err: any) {
      showError(err.message || "Failed to disable compliance type");
    } finally {
      // Remove from loading set
      setDisablingTypes((prev) => {
        const next = new Set(prev);
        next.delete(type.id);
        return next;
      });
    }
  };

  const loading = loadingMasterTypes;
  const error = errorMasterTypes;

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6 p-8">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="neutral-secondary"
              icon={<FeatherArrowLeft />}
              onClick={() => navigate("/admin/dashboard?tab=compliance")}
            >
              Back
            </Button>
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Compliance Types
              </span>
              <span className="text-body font-body text-subtext-color">
                Enable or disable compliance types for your company
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="flex w-full flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
            <span className="text-sm font-medium text-red-800">
              {error.message}
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex w-full items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-body font-body text-subtext-color">
                Loading compliance types...
              </p>
            </div>
          </div>
        ) : masterTypes.length === 0 ? (
          <div className="flex w-full items-center justify-center p-8 text-body font-body text-subtext-color">
            No compliance types found.
          </div>
        ) : (
          <Table
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>Compliance Type</Table.HeaderCell>
                <Table.HeaderCell>Compliance Name</Table.HeaderCell>
                <Table.HeaderCell>Authority</Table.HeaderCell>
                <Table.HeaderCell>Frequency</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.HeaderRow>
            }
          >
            {masterTypes.map((type) => {
              const enabled = type.is_enabled ?? false; // Use is_enabled from master types response
              const isEnabling = enablingTypes.has(type.id);
              const isDisabling = disablingTypes.has(type.id);
              const isLoading = isEnabling || isDisabling;

              return (
                <Table.Row key={type.id}>
                  <Table.Cell>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {type.compliance_type}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-neutral-500">
                      {type.compliance_name}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-neutral-500">
                      {type.authority}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-neutral-500">
                      {type.frequency}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant={enabled ? "success" : "error"}>
                      {enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    {enabled ? (
                      <Button
                        variant="destructive-tertiary"
                        size="small"
                        icon={isDisabling ? undefined : <FeatherX />}
                        onClick={() => handleDisable(type)}
                        disabled={isLoading}
                      >
                        {isDisabling ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Disabling...
                          </span>
                        ) : (
                          "Disable"
                        )}
                      </Button>
                    ) : (
                      <Button
                        variant="brand-secondary"
                        size="small"
                        icon={isEnabling ? undefined : <FeatherCheck />}
                        onClick={() => handleEnable(type)}
                        disabled={isLoading}
                      >
                        {isEnabling ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Enabling...
                          </span>
                        ) : (
                          "Enable"
                        )}
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table>
        )}
      </div>
    </DefaultPageLayout>
  );
};
